import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { useAppDispatch } from '@/store/store-hooks'
import { type MedChronSourceRef } from './schemas'
import { openMedChronSourceViewer, type OpenMedChronSourceViewerOptions } from './open-med-chron-source-viewer'

type SourceLoadError = {
  sourceId: string
  message: string
} | null

function buildMedChronOpenTelemetry(source: MedChronSourceRef, options: OpenMedChronSourceViewerOptions) {
  return {
    tags: {
      contentViewerFeature: 'medical_chronology',
      sourceProvider: source.provider,
      referenceBacked: String(Boolean(options.reference)),
    },
    extra: {
      sourceUserFileId: source.source_id,
      referenceId: options.reference?.id ?? null,
    },
  }
}

export function useOpenMedChronSource() {
  const dispatch = useAppDispatch()
  const [sourceLoadError, setSourceLoadError] = useState<SourceLoadError>(null)

  const openMedChronSource = useCallback(
    async (source: MedChronSourceRef, options: OpenMedChronSourceViewerOptions = {}) => {
      setSourceLoadError(null)

      try {
        const result = await openMedChronSourceViewer({
          source,
          options,
          dispatch,
        })

        if (!result.opened) {
          const telemetry = buildMedChronOpenTelemetry(source, options)
          Sentry.captureMessage('Medical chronology source viewer open failed', {
            level: 'warning',
            tags: telemetry.tags,
            extra: {
              ...telemetry.extra,
              errorMessage: result.error,
            },
          })
          setSourceLoadError({ sourceId: source.source_id, message: result.error })
          return false
        }

        dispatch(ContentViewerActions.setContentViewerSession({ session: result.session }))
        return true
      } catch (error) {
        Sentry.captureException(error, buildMedChronOpenTelemetry(source, options))
        setSourceLoadError({ sourceId: source.source_id, message: 'Could not load this source file.' })
        return false
      }
    },
    [dispatch]
  )

  return {
    openMedChronSource,
    sourceLoadError,
  }
}
